import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  constants,
  isEmptyCollection,
  SessionStorageManager,
  SiteConfigManager,
  useApplicationState,
  useDetection,
  useTranslation,
} from '@atlas/core';
import { PREDEFINED_FORMS } from '@atlas/react-forms';
import { DefaultPopup, Label } from '@atlas/ui';

import { DATA_LAYER_STORAGE_KEYS } from 'common/constants';
import FormMobile from 'common/Form/FormMobile';
import ModalPortal from 'common/ModalPortal/ModalPortal';
import { useDepositBonusContext } from 'components/ClientProfile/Payment/Deposit/DepositBonusContext';
import PaymentMethodItem from 'components/ClientProfile/Payment/Deposit/PaymentMethodItem';
import PaymentFormFooter from 'components/ClientProfile/Payment/PaymentFormFooter';
import SeoPageText from 'components/SeoPage/SeoPageText';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import CommissionsBlock from 'src/components/ClientProfile/Payment/CommissionsBlock';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import useFormatter from 'src/hooks/useFormatter';

import { PaymentProvider } from '../PaymentProvider';

import { useDepositAmounts } from './hooks/useDepositAmounts';
import { useDepositNavigation } from './hooks/useDepositNavigation';
import { useDepositSubmit } from './hooks/useDepositSubmit';
import DepositAmountsBlock from './DepositAmountsBlock';
import DepositBonuses from './DepositBonuses';
import DepositPaymentMethodsDropdown from './DepositPaymentMethodsDropdown';

const DepositPayment = ({
  paymentMethod = null,
  paymentMethods,
  setPaymentMethod,
  setPaymentUrl,
}) => {
  const ref = useRef(null);
  const translate = useTranslation();
  const isAccountBlocked = useAccountBlocked();
  const { account, wallet } = useApplicationState();
  const { browser, ip, locale, os } = useDetection();
  const { currency } = wallet;
  const { currencyFormatter } = useFormatter();

  const preselectedPaymentMethod = SiteConfigManager.getConfig(
    'config.config.mobile.preselected_payment_method',
  );
  const depositAmountTemplate = SiteConfigManager.getConfig(
    'config.config.accountProfile.depositAmountTemplate',
  );

  const { activatedBonus, amountToGetBonus, depositBonusesToShow, isLoading } =
    useDepositBonusContext();

  const regulationContext = useSelfRegulationContext();
  const isCashboxDepositClicked = SessionStorageManager.getItem(
    DATA_LAYER_STORAGE_KEYS.CASHBOX_DEPOSIT_CLICKED,
  );

  const { backUrl, push } = useDepositNavigation({
    account,
    paymentMethod,
    preselectedPaymentMethod,
  });

  const { defaultDepositAmount, depositAmounts } = useDepositAmounts({
    currency,
    depositAmountTemplate,
    paymentMethod,
    preselectedPaymentMethod,
    ref,
  });

  const { action, submitHandler } = useDepositSubmit({
    account,
    backUrl,
    browser,
    currency,
    ip,
    isAccountBlocked,
    isCashboxDepositClicked,
    locale,
    os,
    paymentMethod,
    push,
    ref,
    setPaymentUrl,
    wallet,
    ...regulationContext,
  });

  const form = useMemo(() => PREDEFINED_FORMS.depositForm(paymentMethod), [paymentMethod]);
  const infoTextKey = `pmid_${paymentMethod?.id}_deposit`;

  const renderFooterContent = useMemo(() => {
    const showBonuses = !isLoading && (activatedBonus || !isEmptyCollection(depositBonusesToShow));

    return (
      <>
        <CommissionsBlock
          operationType={constants.PAYMENT_OPERATION.DEPOSIT}
          paymentId={paymentMethod?.id}
        />
        {showBonuses && <DepositBonuses />}
      </>
    );
  }, [isLoading, activatedBonus, depositBonusesToShow, paymentMethod?.id]);

  return (
    <ModalPortal>
      <DefaultPopup
        className="payment-method-popup"
        headerTitle="Deposit"
        headerTitlePosition="left"
        onClose={() => push(backUrl)}
      >
        <div className="popup__content">
          {preselectedPaymentMethod && paymentMethods.length > 1 ? (
            <DepositPaymentMethodsDropdown
              paymentMethod={paymentMethod}
              paymentMethods={paymentMethods}
              setPaymentMethod={setPaymentMethod}
            />
          ) : (
            <div className="profile-payments__block profile-payments__deposit-block">
              <PaymentMethodItem paymentMethod={paymentMethod} />
            </div>
          )}
          {translate[infoTextKey] && (
            <Label className="profile-payments__info-text" message={infoTextKey} />
          )}

          <div className="form-container form-container-payment">
            <PaymentProvider selectedPayment={paymentMethod}>
              <FormMobile
                action={action}
                additionalValues={{ pmId: paymentMethod?.id }}
                buttonText="deposit"
                centerContent={
                  amountToGetBonus > 0 ? (
                    <Label
                      className="deposit-bonus-info"
                      message="quick_deposit_add_amount_to_get_bonus"
                      params={{
                        amount: currencyFormatter.format(amountToGetBonus),
                      }}
                    />
                  ) : null
                }
                footerContent={renderFooterContent}
                form={form}
                onSubmit={submitHandler}
                ref={ref}
                trailingCenterContent={
                  <DepositAmountsBlock
                    defaultDepositAmount={defaultDepositAmount}
                    depositAmounts={depositAmounts}
                    ref={ref}
                  />
                }
              />
            </PaymentProvider>
            <PaymentFormFooter staticBlockSource="payment-guide" />
          </div>
          <SeoPageText />
        </div>
      </DefaultPopup>
    </ModalPortal>
  );
};

DepositPayment.propTypes = {
  paymentMethod: PropTypes.object,
  paymentMethods: PropTypes.array,
  setPaymentMethod: PropTypes.func,
  setPaymentUrl: PropTypes.func.isRequired,
};

export default DepositPayment;
